/**
 * Small, Fabric-free canvas utilities. Extracted from `useFabricCanvas`
 * so they can be unit-tested without spinning up a Fabric.js canvas.
 *
 * Add new helpers here only when they're pure (no DOM dependencies
 * beyond `HTMLCanvasElement` / its 2D context) AND would otherwise
 * sit inline in the 14k-line `useFabricCanvas` file with no test
 * coverage.
 */

export interface BBoxLike {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Return `true` when every pixel of the given canvas is fully
 * transparent (alpha === 0). Reads the entire bitmap in one
 * `getImageData` call and walks the alpha channel only. Used by the
 * marquee-bound action toasts to detect "nothing to cut / copy /
 * delete here" without false positives from anti-aliased edges or
 * 1-bit residual alpha.
 *
 * Returns `true` (vacuously) for a zero-sized canvas or when no 2D
 * context is available — both represent "no content to test" and the
 * caller's "nothing to act on" branch is the right response.
 */
export function isCanvasFullyTransparent(canvas: HTMLCanvasElement): boolean {
  const w = canvas.width;
  const h = canvas.height;
  if (w <= 0 || h <= 0) return true;
  const ctx = canvas.getContext('2d');
  if (!ctx) return true;
  const data = ctx.getImageData(0, 0, w, h).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] !== 0) return false;
  }
  return true;
}

/**
 * Return `true` when bounding boxes `a` and `b` share no overlapping
 * pixels. Uses left/top + width/height (NOT right/bottom inclusive
 * edges), so a rect at `(0, 0, 10, 10)` and another at `(10, 0, 10,
 * 10)` are disjoint — they share an edge but no area.
 *
 * Used to short-circuit marquee-bound paint ops when the stroke's
 * bbox can't possibly affect any pixel inside the marquee.
 */
export function bboxesDisjoint(a: BBoxLike, b: BBoxLike): boolean {
  return (
    a.left + a.width <= b.left ||
    b.left + b.width <= a.left ||
    a.top + a.height <= b.top ||
    b.top + b.height <= a.top
  );
}

/**
 * Clear pixels in a rectangular region of a surface canvas, where the
 * region is supplied in SCENE coords and the surface is positioned at
 * `(surfaceLeft, surfaceTop)` in the same scene. The cleared pixels
 * are at surface-local `(rect.left - surfaceLeft, rect.top -
 * surfaceTop, rect.width, rect.height)` — clipped to the surface's
 * pixel bounds automatically by `clearRect`.
 *
 * Extracted so the offset-aware clip-clear math has a unit-tested
 * home. The cut / marquee-delete flow in `useFabricCanvas` used to do
 * `ctx.translate(-surfaceLeft, -surfaceTop)` then `clearRect(0, 0, W,
 * H)`, which actually cleared canvas pixels at `(-surfaceLeft,
 * -surfaceTop, W, H)` — i.e. outside the surface's pixel bounds for
 * any non-zero offset — so cut silently no-op'd on moved layers.
 * Keeping the math in one place + tested prevents that class of
 * regression.
 *
 * Non-rect marquee shapes still need a Path2D clip; this rect-only
 * path covers the most common shape and is the one whose regression
 * the user reported.
 */
export function clearSurfaceRegion(
  ctx: CanvasRenderingContext2D,
  surfaceLeft: number,
  surfaceTop: number,
  rect: BBoxLike,
): void {
  ctx.clearRect(rect.left - surfaceLeft, rect.top - surfaceTop, rect.width, rect.height);
}
